import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDiagramCommentsQuery } from './useCommentsQueries';
import {
  useCreateRootCommentMutation,
  useReplyToCommentMutation,
  useResolveCommentMutation,
  useReopenCommentMutation,
  useUpdateCommentMutation,
  useSoftDeleteCommentMutation,
} from './useCommentsMutations';
import { commentsKeys } from './commentsKeys';
import type {
  CommentNode,
  CreateRootCommentPayload,
  CreateReplyPayload,
  UpdateCommentPayload,
} from '../../domains/comments/models/Comment';
import type { CommentNodeSnapshot, CommentRealtimeEventType } from '../../domains/comments/models/CommentRealtime';
import { CommentRealtimeChannel } from '../../domains/comments/realtime/CommentRealtimeChannel';
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext';
import { useCommentOverlayStore, type CommentPromptState } from '../../stores/commentOverlayStore';
import { getGravityUserId } from '../useAuth';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';

type CommentListSnapshot = CommentNode[] | undefined;

type CreateRootCommentOptimisticContext = {
  previous: CommentListSnapshot;
  diagramId: string;
  optimisticId: string;
  prompt: CommentPromptState | null;
  draft: string;
  previousActiveCommentId: string | null;
};

type ReplyOptimisticContext = {
  previous: CommentListSnapshot;
  diagramId: string;
  optimisticId: string;
  previousActiveCommentId: string | null;
  threadRootId: string;
};

type SimpleOptimisticContext = {
  previous: CommentListSnapshot;
};

type DeleteOptimisticContext = {
  previous: CommentListSnapshot;
  previousActiveCommentId: string | null;
};

type UpdateOptimisticContext = SimpleOptimisticContext & {
  previousActiveCommentId: string | null;
};

interface UseDiagramCommentsResult {
  comments: CommentNode[];
  isLoading: boolean;
  createRootComment: (input: CreateRootCommentPayload) => Promise<void>;
  createReply: (parentId: string, input: CreateReplyPayload) => Promise<void>;
  isCreating: boolean;
  isReplying: boolean;
  resolveComment: (commentId: string) => Promise<void>;
  reopenComment: (commentId: string) => Promise<void>;
  isResolving: boolean;
  isReopening: boolean;
  updateComment: (commentId: string, input: UpdateCommentPayload) => Promise<void>;
  isUpdating: boolean;
  softDeleteComment: (commentId: string) => Promise<void>;
  isDeleting: boolean;
}

export function useDiagramComments(
  diagramId: string | undefined,
  currentUserId?: string,
): UseDiagramCommentsResult {
  const queryClient = useQueryClient();
  const { data, isLoading } = useDiagramCommentsQuery(diagramId ?? '', undefined, {
    enabled: Boolean(diagramId),
  });
  const effectiveUserId = useMemo(() => {
    const trimmed = currentUserId?.trim();
    if (trimmed) {
      return trimmed.toLowerCase();
    }
    const token = getGravityUserId();
    if (token) {
      return token.toLowerCase();
    }
    return 'anonymous';
  }, [currentUserId]);

  const { awareness } = useDiagramDocument();
  const channelRef = useRef<CommentRealtimeChannel | null>(null);

  useEffect(() => {
    if (!awareness) {
      if (channelRef.current) {
        channelRef.current.destroy();
        channelRef.current = null;
      }
      return;
    }

    const channel = new CommentRealtimeChannel(awareness);
    channelRef.current = channel;

    return () => {
      channel.destroy();
      if (channelRef.current === channel) {
        channelRef.current = null;
      }
    };
  }, [awareness]);

  const publishEvent = useCallback(
    (type: CommentRealtimeEventType, commentId: string, threadRootId: string, options?: { comment?: CommentNode }) => {
      if (!diagramId) {
        return;
      }
      const channel = channelRef.current;
      if (!channel) {
        return;
      }
      const payloadComment = options?.comment ? toCommentSnapshot(options.comment) : undefined;
      channel.publish({
        id: generateRealtimeEventId(),
        type,
        diagramId,
        threadRootId,
        commentId,
        authorId: options?.comment?.authorId,
        timestamp: Date.now(),
        payload: payloadComment ? { comment: payloadComment } : undefined,
      });
    },
    [diagramId],
  );

  useEffect(() => {
    const channel = channelRef.current;
    if (!channel || !diagramId) {
      return;
    }
    const queryKey = commentsKeys.diagram(diagramId);

    const unsubscribe = channel.subscribe((event) => {
      if (event.diagramId !== diagramId) {
        return;
      }

      switch (event.type) {
        case 'comment:create':
        case 'comment:reply': {
          const snapshot = event.payload?.comment;
          if (!snapshot) {
            queryClient.invalidateQueries({ queryKey });
            return;
          }
          queryClient.setQueryData<CommentNode[]>(queryKey, (existing) =>
            mergeRemoteComment(existing, fromCommentSnapshot(snapshot)),
          );
          return;
        }
        case 'comment:delete':
        case 'comment:update':
        case 'comment:resolve':
        case 'comment:reopen': {
          queryClient.invalidateQueries({ queryKey });
          return;
        }
        default:
          return;
      }
    });

    return unsubscribe;
  }, [diagramId, queryClient, awareness]);

  const createMutation = useCreateRootCommentMutation({
    onMutate: async ({ diagramId: mutationDiagramId, input }) => {
      if (!mutationDiagramId) {
        return undefined;
      }

      const queryKey = commentsKeys.diagram(mutationDiagramId);
      await queryClient.cancelQueries({ queryKey });

      const previous = cloneCommentTree(queryClient.getQueryData<CommentNode[]>(queryKey));
      const overlayStore = useCommentOverlayStore.getState();
      const promptSnapshot = clonePromptState(overlayStore.prompt);
      const draftSnapshot = overlayStore.draft;
      const previousActiveCommentId = overlayStore.activeCommentId;

      const nowIso = new Date().toISOString();
      const optimisticComment: CommentNode = {
        id: input.id,
        diagramId: mutationDiagramId,
        shapeId: input.shapeId ?? null,
        threadRootId: input.id,
        parentId: null,
        authorId: effectiveUserId,
        author: {
          id: effectiveUserId,
          name: null,
          email: null,
        },
        body: input.body,
        state: 'open',
        mentions: input.mentions ?? [],
        locOnShape: input.locOnShape
          ? {
              x: { ...input.locOnShape.x },
              y: { ...input.locOnShape.y },
            }
          : null,
        locOnDiagram: input.locOnDiagram ? { ...input.locOnDiagram } : null,
        metadata: input.metadata ? { ...input.metadata } : {},
        resolvedAt: null,
        resolvedBy: null,
        deletedAt: null,
        createdAt: nowIso,
        updatedAt: nowIso,
        replies: [],
      };

      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) {
          return [optimisticComment];
        }
        if (existing.some((item) => item.id === optimisticComment.id)) {
          return existing;
        }
        return [...existing, optimisticComment];
      });

      overlayStore.selectComment(optimisticComment.id);

      return {
        previous,
        diagramId: mutationDiagramId,
        optimisticId: optimisticComment.id,
        prompt: promptSnapshot,
        draft: draftSnapshot,
        previousActiveCommentId,
      } satisfies CreateRootCommentOptimisticContext;
    },
    onError: (_error, _variables, context) => {
      const ctx = context as CreateRootCommentOptimisticContext | undefined;
      if (!ctx?.diagramId) {
        return;
      }
      queryClient.setQueryData(commentsKeys.diagram(ctx.diagramId), ctx.previous);
      const overlayStore = useCommentOverlayStore.getState();
      if (ctx.prompt) {
        overlayStore.openPrompt(ctx.prompt);
        overlayStore.setDraft(ctx.draft ?? '');
      } else {
        overlayStore.clearActiveComment();
        if (ctx.previousActiveCommentId) {
          overlayStore.selectComment(ctx.previousActiveCommentId);
        }
      }
    },
    onSuccess: (comment, _variables, context) => {
      if (!diagramId) {
        return;
      }
      const queryKey = commentsKeys.diagram(diagramId);
      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) {
          return [comment];
        }
        const ctx = context as CreateRootCommentOptimisticContext | undefined;
        const targetId = ctx?.optimisticId ?? comment.id;
        let found = false;
        const next = existing.map((item) => {
          if (item.id === targetId) {
            found = true;
            const mergedReplies = comment.replies && comment.replies.length > 0 ? comment.replies : item.replies;
            return {
              ...comment,
              replies: mergedReplies,
            };
          }
          return item;
        });
        if (!found) {
          return [...next, comment];
        }
        return next;
      });
      const overlayStore = useCommentOverlayStore.getState();
      const ctx = context as CreateRootCommentOptimisticContext | undefined;
      const expectedId = ctx?.optimisticId ?? comment.id;
      if (overlayStore.activeCommentId === expectedId) {
        overlayStore.selectComment(comment.id);
      }
      publishEvent('comment:create', comment.id, comment.threadRootId, { comment });
      
      // Track comment creation (aggregated + first-time)
      SessionMetrics.increment('comments_created');
      SessionMetrics.firstTime('comment_created');
    },
    onSettled: (_result, _error, variables) => {
      const targetDiagramId = variables?.diagramId ?? diagramId;
      if (!targetDiagramId) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: commentsKeys.diagram(targetDiagramId) });
    },
  });

  const replyMutation = useReplyToCommentMutation({
    onMutate: async ({ parentId, input }) => {
      if (!diagramId) {
        return undefined;
      }
      const queryKey = commentsKeys.diagram(diagramId);
      await queryClient.cancelQueries({ queryKey });

      const previous = cloneCommentTree(queryClient.getQueryData<CommentNode[]>(queryKey));
      const parent = findCommentById(previous, parentId);
      const threadRootId = parent?.threadRootId ?? parent?.id ?? parentId;
      const nowIso = new Date().toISOString();
      const optimisticReply: CommentNode = {
        id: input.id,
        diagramId,
        shapeId: parent?.shapeId ?? null,
        threadRootId,
        parentId,
        authorId: effectiveUserId,
        author: {
          id: effectiveUserId,
          name: null,
          email: null,
        },
        body: input.body,
        state: 'open',
        mentions: input.mentions ?? [],
        locOnShape: parent?.locOnShape
          ? {
              x: { ...parent.locOnShape.x },
              y: { ...parent.locOnShape.y },
            }
          : null,
        locOnDiagram: parent?.locOnDiagram ? { ...parent.locOnDiagram } : null,
        metadata: input.metadata
          ? { ...input.metadata }
          : parent?.metadata
            ? { ...parent.metadata }
            : {},
        resolvedAt: null,
        resolvedBy: null,
        deletedAt: null,
        createdAt: nowIso,
        updatedAt: nowIso,
        replies: [],
      };

      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) {
          return existing;
        }
        return existing.map((root) => attachReplyToThread(root, optimisticReply, parentId));
      });

      const overlayStore = useCommentOverlayStore.getState();
      const previousActiveCommentId = overlayStore.activeCommentId;
      overlayStore.selectComment(threadRootId);

      return {
        previous,
        diagramId,
        optimisticId: optimisticReply.id,
        previousActiveCommentId,
        threadRootId,
      } satisfies ReplyOptimisticContext;
    },
    onError: (_error, _variables, context) => {
      if (!diagramId || !context) {
        return;
      }
      const ctx = context as ReplyOptimisticContext;
      queryClient.setQueryData(commentsKeys.diagram(diagramId), ctx.previous);
      const overlayStore = useCommentOverlayStore.getState();
      if (ctx.previousActiveCommentId) {
        overlayStore.selectComment(ctx.previousActiveCommentId);
      }
    },
    onSuccess: (reply, variables, context) => {
      if (!diagramId) {
        return;
      }
      const queryKey = commentsKeys.diagram(diagramId);
      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) {
          return existing;
        }
        const updated = existing.map((root) => updateThreadNodeWithReplacement(root, reply));
        return updated;
      });
      const overlayStore = useCommentOverlayStore.getState();
      const targetThreadId = reply.threadRootId ?? variables.parentId;
      const ctx = context as ReplyOptimisticContext | undefined;
      const expectedId = ctx?.threadRootId ?? targetThreadId;
      if (overlayStore.activeCommentId === expectedId) {
        overlayStore.selectComment(targetThreadId);
      }
      publishEvent('comment:reply', reply.id, reply.threadRootId, { comment: reply });
      
      // Track comment reply (aggregated)
      SessionMetrics.increment('comments_created');
    },
    onSettled: () => {
      if (!diagramId) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: commentsKeys.diagram(diagramId) });
    },
  });

  const comments = useMemo(() => data ?? [], [data]);

  const resolveMutation = useResolveCommentMutation({
    onMutate: async ({ commentId }) => {
      if (!diagramId) {
        return undefined;
      }
      const queryKey = commentsKeys.diagram(diagramId);
      await queryClient.cancelQueries({ queryKey });
      const previous = cloneCommentTree(queryClient.getQueryData<CommentNode[]>(queryKey));
      const nowIso = new Date().toISOString();
      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) {
          return existing;
        }
        return updateCommentList(existing, commentId, (current) => ({
          ...current,
          state: 'resolved',
          resolvedAt: nowIso,
          resolvedBy: effectiveUserId,
          updatedAt: nowIso,
        }));
      });

      return { previous } satisfies SimpleOptimisticContext;
    },
    onError: (_error, _variables, context) => {
      if (!diagramId) {
        return;
      }
      const ctx = context as SimpleOptimisticContext | undefined;
      queryClient.setQueryData(commentsKeys.diagram(diagramId), ctx?.previous);
    },
    onSuccess: (updated) => {
      if (!diagramId) {
        return;
      }
      const queryKey = commentsKeys.diagram(diagramId);
      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) return existing;
        return existing.map((root) => updateThreadNode(root, updated));
      });
      publishEvent('comment:resolve', updated.id, updated.threadRootId, { comment: updated });
    },
    onSettled: () => {
      if (!diagramId) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: commentsKeys.diagram(diagramId) });
    },
  });

  const reopenMutation = useReopenCommentMutation({
    onMutate: async ({ commentId }) => {
      if (!diagramId) {
        return undefined;
      }
      const queryKey = commentsKeys.diagram(diagramId);
      await queryClient.cancelQueries({ queryKey });
      const previous = cloneCommentTree(queryClient.getQueryData<CommentNode[]>(queryKey));
      const nowIso = new Date().toISOString();

      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) return existing;
        return updateCommentList(existing, commentId, (current) => ({
          ...current,
          state: 'open',
          resolvedAt: null,
          resolvedBy: null,
          updatedAt: nowIso,
        }));
      });

      return { previous } satisfies SimpleOptimisticContext;
    },
    onError: (_error, _variables, context) => {
      if (!diagramId) {
        return;
      }
      const ctx = context as SimpleOptimisticContext | undefined;
      queryClient.setQueryData(commentsKeys.diagram(diagramId), ctx?.previous);
    },
    onSuccess: (updated) => {
      if (!diagramId) {
        return;
      }
      const queryKey = commentsKeys.diagram(diagramId);
      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) return existing;
        return existing.map((root) => updateThreadNode(root, updated));
      });
      publishEvent('comment:reopen', updated.id, updated.threadRootId, { comment: updated });
    },
    onSettled: () => {
      if (!diagramId) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: commentsKeys.diagram(diagramId) });
    },
  });

  const updateMutation = useUpdateCommentMutation({
    onMutate: async ({ commentId, input }) => {
      if (!diagramId) {
        return undefined;
      }
      const queryKey = commentsKeys.diagram(diagramId);
      await queryClient.cancelQueries({ queryKey });
      const previous = cloneCommentTree(queryClient.getQueryData<CommentNode[]>(queryKey));
      const nowIso = new Date().toISOString();

      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) return existing;
        return updateCommentList(existing, commentId, (current) => {
          const nextMetadata =
            input.metadata !== undefined
              ? input.metadata
                ? { ...input.metadata }
                : {}
              : current.metadata
              ? { ...current.metadata }
              : {};

          const nextShapeId = Object.prototype.hasOwnProperty.call(input, 'shapeId')
            ? input.shapeId ?? null
            : current.shapeId ?? null;

          const nextLocOnShape = Object.prototype.hasOwnProperty.call(input, 'locOnShape')
            ? input.locOnShape
              ? {
                  x: { ...input.locOnShape.x },
                  y: { ...input.locOnShape.y },
                }
              : null
            : current.locOnShape
            ? {
                x: { ...current.locOnShape.x },
                y: { ...current.locOnShape.y },
              }
            : null;

          const nextLocOnDiagram = Object.prototype.hasOwnProperty.call(input, 'locOnDiagram')
            ? input.locOnDiagram
              ? { ...input.locOnDiagram }
              : null
            : current.locOnDiagram
            ? { ...current.locOnDiagram }
            : null;

          return {
            ...current,
            body: input.body ?? current.body,
            metadata: nextMetadata,
            shapeId: nextShapeId,
            locOnShape: nextLocOnShape,
            locOnDiagram: nextLocOnDiagram,
            updatedAt: nowIso,
          };
        });
      });

      const overlayStore = useCommentOverlayStore.getState();
      const previousActiveCommentId = overlayStore.activeCommentId;

      return { previous, previousActiveCommentId } satisfies UpdateOptimisticContext;
    },
    onError: (_error, _variables, context) => {
      if (!diagramId) {
        return;
      }
      const ctx = context as UpdateOptimisticContext | undefined;
      queryClient.setQueryData(commentsKeys.diagram(diagramId), ctx?.previous);
      if (ctx?.previousActiveCommentId) {
        useCommentOverlayStore.getState().selectComment(ctx.previousActiveCommentId);
      }
    },
    onSuccess: (updated, _variables, context) => {
      if (!diagramId) {
        return;
      }
      const queryKey = commentsKeys.diagram(diagramId);
      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) return existing;
        return existing.map((root) => updateThreadNode(root, updated));
      });
      const overlayStore = useCommentOverlayStore.getState();
      const ctx = context as UpdateOptimisticContext | undefined;
      const fallbackTargetId = updated.threadRootId ?? updated.id;
      const expectedActiveId = ctx?.previousActiveCommentId ?? fallbackTargetId;
      const currentActiveId = overlayStore.activeCommentId;
      if (currentActiveId && (currentActiveId === expectedActiveId || currentActiveId === fallbackTargetId)) {
        overlayStore.selectComment(fallbackTargetId);
      }
      publishEvent('comment:update', updated.id, updated.threadRootId, { comment: updated });
    },
    onSettled: () => {
      if (!diagramId) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: commentsKeys.diagram(diagramId) });
    },
  });

  const softDeleteMutation = useSoftDeleteCommentMutation({
    onMutate: async ({ commentId }) => {
      if (!diagramId) {
        return undefined;
      }
      const queryKey = commentsKeys.diagram(diagramId);
      await queryClient.cancelQueries({ queryKey });
      const previous = cloneCommentTree(queryClient.getQueryData<CommentNode[]>(queryKey));
      const overlayStore = useCommentOverlayStore.getState();
      const previousActiveCommentId = overlayStore.activeCommentId;

      queryClient.setQueryData<CommentNode[]>(queryKey, (existing) => {
        if (!existing) return existing;
        return removeCommentFromList(existing, commentId);
      });

      if (previousActiveCommentId === commentId) {
        overlayStore.clearActiveComment();
      }

      return { previous, previousActiveCommentId } satisfies DeleteOptimisticContext;
    },
    onError: (_error, _variables, context) => {
      if (!diagramId) {
        return;
      }
      const ctx = context as DeleteOptimisticContext | undefined;
      queryClient.setQueryData(commentsKeys.diagram(diagramId), ctx?.previous);
      if (ctx?.previousActiveCommentId) {
        useCommentOverlayStore.getState().selectComment(ctx.previousActiveCommentId);
      }
    },
    onSuccess: (_result, variables) => {
      if (!variables?.commentId) {
        return;
      }
      publishEvent('comment:delete', variables.commentId, variables.commentId);
    },
    onSettled: () => {
      if (!diagramId) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: commentsKeys.diagram(diagramId) });
    },
  });

  const createRootComment = async (input: CreateRootCommentPayload) => {
    if (!diagramId) {
      throw new Error('diagramId is required to create a comment');
    }
    await createMutation.mutateAsync({ diagramId, input });
  };

  const createReply = async (parentId: string, input: CreateReplyPayload) => {
    if (!diagramId) {
      throw new Error('diagramId is required to reply to a comment');
    }
    await replyMutation.mutateAsync({ diagramId, parentId, input });
  };

  const resolveComment = async (commentId: string) => {
    await resolveMutation.mutateAsync({ commentId });
  };

  const reopenComment = async (commentId: string) => {
    await reopenMutation.mutateAsync({ commentId });
  };

  const updateComment = async (commentId: string, input: UpdateCommentPayload) => {
    await updateMutation.mutateAsync({ commentId, input });
  };

  const softDeleteComment = async (commentId: string) => {
    await softDeleteMutation.mutateAsync({ commentId });
  };

  return {
    comments,
    isLoading,
    createRootComment,
    createReply,
    isCreating: createMutation.isPending,
    isReplying: replyMutation.isPending,
    resolveComment,
    reopenComment,
    isResolving: resolveMutation.isPending,
    isReopening: reopenMutation.isPending,
    updateComment,
    isUpdating: updateMutation.isPending,
    softDeleteComment,
    isDeleting: softDeleteMutation.isPending,
  };
}

function generateRealtimeEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `comment-event-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function toCommentSnapshot(comment: CommentNode): CommentNodeSnapshot {
  return {
    id: comment.id,
    diagramId: comment.diagramId,
    threadRootId: comment.threadRootId,
    parentId: comment.parentId,
    authorId: comment.authorId,
    author: comment.author ? { ...comment.author } : undefined,
    body: comment.body,
    state: comment.state,
    mentions: comment.mentions ? [...comment.mentions] : [],
    metadata: comment.metadata ? { ...comment.metadata } : {},
    shapeId: comment.shapeId,
    locOnShape: comment.locOnShape
      ? {
          x: { ...comment.locOnShape.x },
          y: { ...comment.locOnShape.y },
        }
      : null,
    locOnDiagram: comment.locOnDiagram ? { ...comment.locOnDiagram } : null,
    resolvedAt: comment.resolvedAt,
    resolvedBy: comment.resolvedBy,
    deletedAt: comment.deletedAt,
    createdAt: comment.createdAt,
    updatedAt: comment.updatedAt,
    replies: comment.replies?.map(toCommentSnapshot) ?? [],
  };
}

export function fromCommentSnapshot(snapshot: CommentNodeSnapshot): CommentNode {
  return {
    id: snapshot.id,
    diagramId: snapshot.diagramId,
    threadRootId: snapshot.threadRootId,
    parentId: snapshot.parentId,
    authorId: snapshot.authorId,
    author: snapshot.author ? { ...snapshot.author } : undefined,
    body: snapshot.body,
    state: snapshot.state,
    mentions: snapshot.mentions ? [...snapshot.mentions] : [],
    metadata: snapshot.metadata ? { ...snapshot.metadata } : {},
    shapeId: snapshot.shapeId,
    locOnShape: snapshot.locOnShape
      ? {
          x: { ...snapshot.locOnShape.x },
          y: { ...snapshot.locOnShape.y },
        }
      : null,
    locOnDiagram: snapshot.locOnDiagram ? { ...snapshot.locOnDiagram } : null,
    resolvedAt: snapshot.resolvedAt,
    resolvedBy: snapshot.resolvedBy,
    deletedAt: snapshot.deletedAt,
    createdAt: snapshot.createdAt,
    updatedAt: snapshot.updatedAt,
    replies: Array.isArray(snapshot.replies) ? snapshot.replies.map(fromCommentSnapshot) : [],
  };
}

export function mergeRemoteComment(existing: CommentNode[] | undefined, incoming: CommentNode): CommentNode[] {
  if (!existing) {
    return [incoming];
  }

  const current = findCommentById(existing, incoming.id);
  if (current) {
    return updateCommentList(existing, incoming.id, () => ({
      ...incoming,
      replies: mergeReplies(current.replies, incoming.replies),
    }));
  }

  if (!incoming.parentId) {
    return existing.some((comment) => comment.id === incoming.id) ? existing : [...existing, incoming];
  }

  const parentId = incoming.parentId ?? incoming.threadRootId;
  let updated = false;
  const next = existing.map((root) => {
    if (root.id !== incoming.threadRootId) {
      return root;
    }
    const result = attachReplyToThread(root, incoming, parentId);
    if (result !== root) {
      updated = true;
    }
    return result;
  });

  if (!updated) {
    return existing;
  }

  return next;
}

function attachReplyToThread(root: CommentNode, reply: CommentNode, parentId: string): CommentNode {
  if (root.id === parentId) {
    return {
      ...root,
      replies: [...root.replies, reply],
    };
  }

  if (root.replies.length === 0) {
    if (root.id === (reply.threadRootId ?? parentId)) {
      return {
        ...root,
        replies: [...root.replies, reply],
      };
    }
    return root;
  }

  const updatedReplies = root.replies.map((child) => attachReplyToThread(child, reply, parentId));
  const changed = updatedReplies.some((child, index) => child !== root.replies[index]);
  if (changed) {
    return {
      ...root,
      replies: updatedReplies,
    };
  }

  if (root.id === (reply.threadRootId ?? parentId)) {
    return {
      ...root,
      replies: [...root.replies, reply],
    };
  }

  return root;
}

function updateThreadNode(root: CommentNode, updated: CommentNode): CommentNode {
  if (root.id === updated.id) {
    return {
      ...root,
      ...updated,
      replies: mergeReplies(root.replies, updated.replies),
    };
  }

  if (root.replies.length === 0) {
    return root;
  }

  const nextReplies = root.replies.map((child) => updateThreadNode(child, updated));
  const changed = nextReplies.some((child, index) => child !== root.replies[index]);
  if (changed) {
    return {
      ...root,
      replies: nextReplies,
    };
  }

  return root.id === updated.threadRootId
    ? {
        ...root,
        replies: nextReplies,
      }
    : root;
}

function updateThreadNodeWithReplacement(root: CommentNode, updated: CommentNode): CommentNode {
  if (root.id === updated.id) {
    return {
      ...root,
      ...updated,
      replies: mergeReplies(root.replies, updated.replies),
    };
  }

  if (root.replies.length === 0) {
    return root;
  }

  const nextReplies = root.replies.map((child) => updateThreadNodeWithReplacement(child, updated));
  const changed = nextReplies.some((child, index) => child !== root.replies[index]);
  if (changed) {
    return {
      ...root,
      replies: nextReplies,
    };
  }

  return root;
}

function updateCommentList(
  comments: CommentNode[],
  commentId: string,
  updater: (comment: CommentNode) => CommentNode,
): CommentNode[] {
  return comments.map((root) => updateCommentNode(root, commentId, updater));
}

function updateCommentNode(
  node: CommentNode,
  commentId: string,
  updater: (comment: CommentNode) => CommentNode,
): CommentNode {
  if (node.id === commentId) {
    return updater(node);
  }

  if (node.replies.length === 0) {
    return node;
  }

  const nextReplies = node.replies.map((child) => updateCommentNode(child, commentId, updater));
  const changed = nextReplies.some((child, index) => child !== node.replies[index]);
  if (changed) {
    return {
      ...node,
      replies: nextReplies,
    };
  }

  return node;
}

function removeCommentFromList(comments: CommentNode[], targetId: string): CommentNode[] {
  const next: CommentNode[] = [];
  for (const comment of comments) {
    const updated = removeCommentNode(comment, targetId);
    if (updated) {
      next.push(updated);
    }
  }
  return next;
}

function removeCommentNode(comment: CommentNode, targetId: string): CommentNode | null {
  if (comment.id === targetId) {
    return null;
  }

  if (comment.replies.length === 0) {
    return comment;
  }

  let changed = false;
  const nextReplies: CommentNode[] = [];
  for (const reply of comment.replies) {
    const updated = removeCommentNode(reply, targetId);
    if (updated !== reply) {
      changed = true;
    }
    if (updated) {
      nextReplies.push(updated);
    }
  }

  if (!changed) {
    return comment;
  }

  return {
    ...comment,
    replies: nextReplies,
  };
}

function cloneCommentTree(comments: CommentNode[] | undefined): CommentNode[] | undefined {
  if (!comments) {
    return undefined;
  }
  return comments.map(cloneCommentNode);
}

function cloneCommentNode(comment: CommentNode): CommentNode {
  return {
    ...comment,
    author: comment.author ? { ...comment.author } : undefined,
    mentions: [...comment.mentions],
    metadata: comment.metadata ? { ...comment.metadata } : {},
    locOnDiagram: comment.locOnDiagram ? { ...comment.locOnDiagram } : null,
    locOnShape: comment.locOnShape
      ? {
          x: { ...comment.locOnShape.x },
          y: { ...comment.locOnShape.y },
        }
      : null,
    replies: comment.replies.map(cloneCommentNode),
  };
}

function findCommentById(comments: CommentNode[] | undefined, commentId: string): CommentNode | null {
  if (!comments) {
    return null;
  }
  for (const comment of comments) {
    if (comment.id === commentId) {
      return comment;
    }
    const child = findCommentById(comment.replies, commentId);
    if (child) {
      return child;
    }
  }
  return null;
}

function clonePromptState(prompt: CommentPromptState | null): CommentPromptState | null {
  if (!prompt) {
    return null;
  }
  return {
    diagram: { ...prompt.diagram },
    local: { ...prompt.local },
    shapeId: prompt.shapeId ?? null,
    locOnShape: prompt.locOnShape
      ? {
          x: { ...prompt.locOnShape.x },
          y: { ...prompt.locOnShape.y },
        }
      : null,
  };
}

function mergeReplies(existing: CommentNode[], incoming: CommentNode[] | undefined): CommentNode[] {
  if (Array.isArray(incoming) && incoming.length > 0) {
    return incoming;
  }
  if (existing.length > 0 && (!incoming || incoming.length === 0)) {
    return existing;
  }
  return incoming ?? existing;
}
